import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import useOrg from '@/hooks/useOrg';
import { userPreferences as prefsApi } from '@/lib/trackizy';

const PREF_KEY = 'saved_map_views_v1';
const LS_KEY = 'trackizy_saved_map_views';
const MAX_VIEWS = 10;

/**
 * useSavedViews — reads/writes named map views to server user preferences,
 * falling back to localStorage when server is unavailable.
 */
export default function useSavedViews() {
  const org = useOrg();
  const user = useSelector((state) => state.session.user);

  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- helpers for localStorage fallback ----
  const readLS = useCallback(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, []);

  const writeLS = useCallback((v) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(v));
    } catch {
      /* quota exceeded — ignore */
    }
  }, []);

  // ---- persist to server ----
  const persistToServer = useCallback(
    async (newViews) => {
      if (!org?.id || !user?.id) return;
      try {
        await prefsApi.set(PREF_KEY, newViews);
      } catch (err) {
        console.warn('[useSavedViews] save error:', err);
      }
    },
    [org?.id, user?.id],
  );

  // ---- load on mount ----
  useEffect(() => {
    if (!org?.id || !user?.id) {
      setViews(readLS());
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await prefsApi.get(PREF_KEY);

        if (cancelled) return;

        if (data?.value && Array.isArray(data.value)) {
          setViews(data.value);
        } else {
          setViews(readLS());
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[useSavedViews] load error:', err);
          setViews(readLS());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [org?.id, user?.id, readLS]);

  // ---- saveView ----
  const saveView = useCallback(
    async ({ name, center, zoom, statusFilter, groupFilter }) => {
      const newView = {
        id: crypto.randomUUID(),
        name,
        center,
        zoom,
        statusFilter: statusFilter ?? null,
        groupFilter: groupFilter ?? null,
        createdAt: new Date().toISOString(),
      };

      const updated = [...views, newView].slice(-MAX_VIEWS);
      setViews(updated);

      if (org?.id && user?.id) {
        await persistToServer(updated);
      } else {
        writeLS(updated);
      }
    },
    [views, persistToServer, writeLS, org?.id, user?.id],
  );

  // ---- deleteView ----
  const deleteView = useCallback(
    async (id) => {
      const updated = views.filter((v) => v.id !== id);
      setViews(updated);

      if (org?.id && user?.id) {
        await persistToServer(updated);
      } else {
        writeLS(updated);
      }
    },
    [views, persistToServer, writeLS, org?.id, user?.id],
  );

  return {
    views,
    loading,
    saveView,
    deleteView,
    isFull: views.length >= MAX_VIEWS,
  };
}
